import { of } from 'rxjs';
import { map, switchMap, catchError, mergeMap, exhaustMap } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import Http from '../../http/http';
import AppSettings from '../../app.settings';

import * as UserActions from './user.actions';
import * as AppActions from '../app/app.actions';
import * as AccountsActions from '../accounts/accounts.actions';
import * as AttributeActions from '../attributes/attributes.action';
import * as SecurityGroupActions from '../security/security.action';
import * as TransferActions from '../transfer/transfer.actions';
import * as FavouriteActions from '../favourite/favourite.action';

const addToDataLayer = (data) => window.dataLayer.push(data);

const getRequiredActions = (permissions = []) => {
  const requiredActions = [
    AttributeActions.fetch(),
    SecurityGroupActions.fetch(),
    FavouriteActions.favouriteFetch(),
  ];

  if (permissions.includes('admin.roles')) {
    requiredActions.push(AccountsActions.getAccountRoles());
  }

  if (permissions.includes('devices.transfer')) {
    requiredActions.push(TransferActions.getTransferKeys());
  }

  return requiredActions;
};

export const RequestUser = (action$) =>
  action$.pipe(
    ofType(UserActions.REQUEST_USER),
    switchMap(() =>
      Http.get('/api/users/me').pipe(
        map((data) => of(UserActions.requestUserSuccess(data))),
        catchError((error) => of(UserActions.requestUserFailed(error))),
      ),
    ),
  );

export const AfterRequestUserSuccess = (action$, state$) =>
  action$.pipe(
    ofType(UserActions.REQUEST_USER_SUCCESS),
    mergeMap(() => {
      const { user } = state$.value;

      // build action array that should run after getting user info and may required permission
      const requiredActions = getRequiredActions(user.permissions);

      const {
        claims: {
          data: { landingPage },
        },
      } = user;
      const page = AppSettings.LandingPages.find((page) => page.id === landingPage);

      if (page) {
        requiredActions.push(AppActions.redirectUser(page.state));
      }

      return of(...requiredActions, AppActions.localized());
    }),
  );

/**
 * get all accounts that user can switch between
 * @param {*} action$
 */
export const RequestUserAccounts = (action$, state$) =>
  action$.pipe(
    ofType(
      UserActions.REQUEST_USER_SUCCESS,
      AccountsActions.CREATE_SUBACCOUNT_SUCCESS,
      AccountsActions.UPDATE_SUBACCOUNT_SUCCESS,
    ),
    switchMap(() =>
      Http.get(`${AppSettings.IcebergApiUserInfoEndpoint}/accounts/user`).pipe(
        map((data) => {
          const isAccessible = (account = {}) =>
            account.effectiveAccessMode && account.effectiveAccessMode !== 'unknown';
          // set property master name  on every child
          const accounts = data.map((masterAcc) => ({
            ...masterAcc,
            accessible: isAccessible(masterAcc),
            accounts: (masterAcc.accounts || []).map((linkedAcc) => ({
              ...linkedAcc,
              accessible: isAccessible(linkedAcc),
              master: masterAcc.name,
            })),
          }));
          const flattenAccounts = accounts.concat(
            ...accounts.map((a) => {
              return a.accounts || [];
            }),
          );
          const currentUser = state$.value.user;
          const currentAccount = flattenAccounts.find((acc) => acc.id === currentUser.account.id);
          const memberShips = flattenAccounts.filter((acc) => isAccessible(acc));

          // create lookup table for current account and it's subaccounts
          const accountNameLookup = (currentAccount.accounts || []).reduce((acc, account) => {
            acc[account.id] = account.name;
            return acc;
          }, {});
          accountNameLookup[currentAccount.id] = currentAccount.name;

          if (currentAccount.attrs) {
            const {
              'genie:providerId': genieProviderId,
              'genie:parentId': genieParentId,
            } = currentAccount.attrs;

            if (genieProviderId || genieParentId) {
              addToDataLayer({
                event: 'accountInfo',
                data: { genieParentId, genieProviderId },
              });
            }
          }

          return UserActions.requestUserAccountsSuccess({
            accounts,
            currentAccount,
            memberShips,
            accountNameLookup,
          });
        }),
        catchError((error) => of(UserActions.requestUserAccountsFail(error))),
      ),
    ),
  );

export const RetrieveDataPointsDescriptors = (action$) =>
  action$.pipe(
    ofType(UserActions.REQUEST_DATAPOINTS_DESCRIPTORS),
    exhaustMap(() =>
      Http.get('/api/datapoints/descriptors').pipe(
        map((data) => UserActions.requestDatapointsDescriptorsSuccess(data)),
        catchError((error) => of(UserActions.requestDatapointsDescriptorsFail(error))),
      ),
    ),
  );
